import {
  DATE_PATTERN_PRESETS,
  DATE_PATTERN_SAMPLE,
  DATE_PATTERN_TOKENS,
  LOCALES,
  countPatternTokens,
  formatDatePattern,
  isDatePattern,
  type DatePatternToken,
  type Locale,
} from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';

import { patternNamesFor } from '@/shared/utils/pattern-names/pattern-names.util';
import { useScalarAnnouncer } from '../useScalarAnnouncer/useScalarAnnouncer';

import { type PatternSelectProps } from './PatternSelect.types';

/** The `<Select>` value that means "I will write my own". */
export const PATTERN_CUSTOM_VALUE = '__custom__';

export interface PatternPreviewRow {
  locale: Locale;
  /** The locale's own name, as the preview row's label. */
  label: string;
  text: string;
}

export interface PatternTokenRow {
  token: DatePatternToken;
  meaning: string;
  sample: string;
}

export type UsePatternSelectLogicInput = Omit<
  PatternSelectProps,
  'id' | 'className' | 'size' | 'aria-describedby'
>;

export interface UsePatternSelectLogic {
  presets: readonly string[];
  /** What the `<Select>` shows: a preset, or the custom sentinel. */
  selected: string;
  isCustom: boolean;
  onSelectChange: (next: string) => void;
  /** The custom box's text — kept even when it does not parse. */
  draft: string;
  onDraftChange: (next: string) => void;
  isDraftInvalid: boolean;
  /** How each preset renders, so the list offers dates rather than syntax. */
  sampleOf: (pattern: string) => string;
  previews: readonly PatternPreviewRow[];
  tokens: readonly PatternTokenRow[];
  /** The pattern is legal but substitutes nothing — every date would look alike. */
  hasNoTokens: boolean;
  warning: string | undefined;
  announcement: string;
  presetLabel: string;
  customLabel: string;
  helpLabel: string;
}

/**
 * A date format, chosen by what it RENDERS rather than by typing syntax.
 *
 * `brand.dateFormat` was a free-text box over a format grammar: an operator
 * typed `DD/MM/YYYY` and never found out what it produced, and `hello` was an
 * accepted value. Here every choice is shown as a rendered date, in **both**
 * locales at once — one pattern serves both audiences and `MMMM` behaves very
 * differently between them — and the custom escape validates against the same
 * grammar the api stores.
 *
 * The preview is drawn at a **fixed** sample instant, so a screenshot taken
 * tonight matches one taken tomorrow and no spec can flake at midnight.
 */
export function usePatternSelectLogic({
  label,
  value,
  onValueChange,
  presets = DATE_PATTERN_PRESETS,
  isReadOnly = false,
  isDisabled = false,
}: UsePatternSelectLogicInput): UsePatternSelectLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const announcer = useScalarAnnouncer();

  // Nothing chosen yet. The empty case has to be its own state: `''` matches no
  // preset, so folding it into "custom" opened an untouched field in custom mode
  // with a red box and a validation error it was born with.
  const isEmpty = value === '';

  // A stored value that matches no preset opens in custom mode by itself — the
  // analogue of a duration demoting to a unit that can express its value.
  //
  // It LATCHES rather than being re-derived each render, and that matters:
  // clearing the box makes the value empty, and a derived flag would decide
  // nothing is chosen and pull the box out from under the operator mid-edit.
  // Only picking a preset leaves custom mode.
  const matchesPreset = presets.includes(value);
  const [isCustom, setIsCustom] = useState(!isEmpty && !matchesPreset);
  if (!isCustom && !isEmpty && !matchesPreset) setIsCustom(true);

  /**
   * The last pattern that was actually usable, kept only so the preview has
   * something true to render while the box holds something that is not.
   *
   * Adjusted during render rather than in an effect — React's own "adjust state
   * when a prop changes" pattern — so the preview never lags a keystroke behind.
   */
  const [lastValid, setLastValid] = useState(value);
  if (isDatePattern(value) && value !== lastValid) setLastValid(value);

  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;
    announcer.announceReadOnlyRefusal();
    return true;
  }, [announcer, isDisabled, isReadOnly]);

  // The operator's own locale, not a pinned English: the list is what they are
  // choosing FROM, so it should read the way their portal reads. The preview
  // strip below still shows both, because the pattern serves both audiences.
  const sampleOf = useCallback(
    (pattern: string): string =>
      formatDatePattern(pattern, DATE_PATTERN_SAMPLE, patternNamesFor(locale)),
    [locale],
  );

  const onSelectChange = useCallback(
    (next: string) => {
      if (refuse()) return;

      if (next === PATTERN_CUSTOM_VALUE) {
        setIsCustom(true);
        return;
      }

      setIsCustom(false);
      onValueChange(next);
    },
    [onValueChange, refuse],
  );

  const onDraftChange = useCallback(
    (next: string) => {
      if (refuse()) return;
      // What the operator typed is reported EVEN WHEN it is not a usable
      // pattern, and that is the important part. Holding it back would leave the
      // form carrying the previous value while the box shows something else, and
      // Save would then write a pattern the operator can see is not what they
      // wrote — the same silent lie `useScalarNumber` avoids by committing
      // `undefined`. The schema refuses it, which keeps validity's one source.
      onValueChange(next);
    },
    [onValueChange, refuse],
  );

  // An empty field is not a WRONG field: the schema refuses it on submit, which
  // is where "you have to choose something" belongs.
  const isDraftInvalid = !isEmpty && !isDatePattern(value);
  // The preview needs a pattern it can render; while the box holds one it
  // cannot, it shows the last one that worked rather than going blank.
  const effective = isDraftInvalid ? lastValid : value;
  const hasNoTokens = !isEmpty && countPatternTokens(effective) === 0;

  // No pattern, no preview: two empty rows under an untouched field are noise.
  const previews = useMemo(
    () =>
      effective === ''
        ? []
        : LOCALES.map((row) => ({
            locale: row,
            label: t(`locale.${row}`),
            text: formatDatePattern(effective, DATE_PATTERN_SAMPLE, patternNamesFor(row)),
          })),
    [effective, t],
  );

  const tokens = useMemo(
    () =>
      DATE_PATTERN_TOKENS.map((token) => ({
        token,
        meaning: t(`pattern.token.${token}`),
        sample: sampleOf(token),
      })),
    [sampleOf, t],
  );

  const warning = isDraftInvalid
    ? t('pattern.invalid')
    : hasNoTokens
      ? t('pattern.noTokens')
      : undefined;

  return useMemo(
    () => ({
      presets,
      selected: isCustom ? PATTERN_CUSTOM_VALUE : value,
      isCustom,
      onSelectChange,
      draft: value,
      onDraftChange,
      isDraftInvalid,
      sampleOf,
      previews,
      tokens,
      hasNoTokens,
      warning,
      announcement: announcer.announcement,
      presetLabel: t('pattern.presetFor', { label }),
      customLabel: t('pattern.customFor', { label }),
      helpLabel: t('pattern.helpFor', { label }),
    }),
    [
      announcer.announcement,
      hasNoTokens,
      isCustom,
      isDraftInvalid,
      label,
      onDraftChange,
      onSelectChange,
      presets,
      previews,
      sampleOf,
      t,
      tokens,
      value,
      warning,
    ],
  );
}
